import React, { useMemo } from 'react'
import PropTypes from 'prop-types'
import { List, Divider, useTheme } from '@mui/material'
import HHSectionPlaceholder from 'components/common/HHSectionPlaceholder'
import { useSelector } from 'react-redux'
import { noop } from 'lodash'
import {
  selectIsAddingEvent,
  selectHasItemsMarkedForDeletion,
  selectItemsMarkedForDeletion,
  selectSelectedDate,
  selectHasEditableItems,
} from 'data/configured-service/configuredServiceEventsSlice'
import EventItem from './EventItem'
import EventActionBar from './EventActionBar'

const EventList = ({
  selectedDayEvents,
  control,
  serviceActionsById,
  getAvailableActionsForEvent,
  onEdit,
  onDelete,
  onUndoDelete,
  onAddEvent,
  onSave = noop,
  onCancel = noop,
  isMutationLoading = false,
  isDraft,
  hideAddEventButton = false,
  isPreview = false,
  disposalCountByWorkOrderId = {},
  autocompleteSlotProps,
  maxHeight = 73 * 4,
}) => {
  const theme = useTheme()
  const itemsMarkedForDeletion = useSelector(selectItemsMarkedForDeletion)
  const selectedDate = useSelector(selectSelectedDate)
  const isAddingEvent = useSelector(selectIsAddingEvent)
  const hasItemsMarkedForDeletion = useSelector(selectHasItemsMarkedForDeletion)
  const hasEditableItems = useSelector(selectHasEditableItems)
  const hasEventsOnCurrentDay = useMemo(() => selectedDayEvents.length > 0, [selectedDayEvents])
  const pendingDeletionCount = useMemo(() => Object.values(itemsMarkedForDeletion || {}).filter(Boolean).length, [itemsMarkedForDeletion])
  const showAddEventButton = !isAddingEvent && !hasItemsMarkedForDeletion && !hideAddEventButton

  const actionBarProps = {
    control,
    getAvailableActionsForEvent,
    selectedDate,
    isAddingEvent,
    hasItemsMarkedForDeletion,
    hasEditableItems,
    pendingDeletionCount,
    isMutationLoading,
    onCancel,
    onSave,
    autocompleteSlotProps,
    onAddEvent,
  }

  if (selectedDayEvents.length > 0) {
    return (
      <>
        <EventActionBar {...actionBarProps} showAddEventButton={showAddEventButton && !hasEditableItems} />
        <List sx={{ maxHeight, overflow: 'auto' }}>
          {selectedDayEvents.map(event => (
            <React.Fragment key={event.id}>
              <EventItem
                event={event}
                control={control}
                serviceActionsById={serviceActionsById}
                getAvailableActionsForEvent={getAvailableActionsForEvent}
                selectedDate={selectedDate}
                onEdit={onEdit || noop}
                onDelete={onDelete || noop}
                onUndoDelete={onUndoDelete || noop}
                isDraft={isDraft}
                isPreview={isPreview}
                disposalCountByWorkOrderId={disposalCountByWorkOrderId}
                autocompleteSlotProps={autocompleteSlotProps}
              />
              <Divider />
            </React.Fragment>
          ))}
        </List>
      </>
    )
  }

  return (
    <>
      <HHSectionPlaceholder
        title="No events scheduled for this date."
        sx={{
          borderColor: theme.palette.primary.light,
        }}
      />
      {!hasItemsMarkedForDeletion && (
        <EventActionBar
          {...actionBarProps}
          showAddEventButton={!isAddingEvent && !hasEditableItems && !hasEventsOnCurrentDay && !hideAddEventButton}
        />
      )}
    </>
  )
}

EventList.propTypes = {
  selectedDayEvents: PropTypes.array.isRequired,
  control: PropTypes.object.isRequired,
  serviceActionsById: PropTypes.object,
  getAvailableActionsForEvent: PropTypes.func.isRequired,
  onEdit: PropTypes.func,
  onDelete: PropTypes.func,
  onUndoDelete: PropTypes.func,
  onAddEvent: PropTypes.func,
  onSave: PropTypes.func,
  onCancel: PropTypes.func,
  isMutationLoading: PropTypes.bool,
  isDraft: PropTypes.bool,
  hideAddEventButton: PropTypes.bool,
  isPreview: PropTypes.bool,
  disposalCountByWorkOrderId: PropTypes.object,
  autocompleteSlotProps: PropTypes.object,
  maxHeight: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
}

export default EventList
